import Footer from '@/components/Footer';
import MetaPixelEvents from '@/components/MetaPixelEvents';
import NavBar from '@/components/NavBar';
import Seo from '@/components/Seo';
import WhatsAppButton, { WhatsAppIcon } from '@/components/WhatsAppButton';
import { CategoryContext } from '@/lib/CategoryContext';
import { whatsAppHref } from '@/lib/utils';
import { Phone } from 'lucide-react';
import { PropsWithChildren, useContext } from 'react';

/*
 * The public shell.
 *
 * The body used to sit on a full-bleed orange->yellow gradient that every page
 * then covered with opaque panels, so it only ever showed through in the
 * gutters. The page now sits on warm chalk (--surface) and pages compose their
 * own rhythm with <Band>.
 */
const AppLayout = ({ children }: PropsWithChildren) => {
  const { phone } = useContext(CategoryContext);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Seo />
      <MetaPixelEvents />
      <NavBar />
      <main className="flex flex-1 flex-col">{children}</main>

      {/*
        Woven brand rule, between the page and the footer.

        The home page closes on an ink CTA and the footer is ink, so the two fused
        into one slab. A hairline was not enough to break it; this is, and it does
        it everywhere rather than only where a dark band happens to land last.

        It carries the phone number because that was the one thing the footer does
        not already say — the footer has a newsletter form but no way to call, and
        for a Trichy business the phone is the channel people actually use.

        Text is ink, not the usual white-on-orange: --primary-foreground over the
        amber end of the gradient measures 1.91:1, far under AA, while ink holds
        5.39:1 at the orange end and 9.23:1 at the amber one.

        min-h-32 (128px) rather than a height driven by content: `phone` arrives
        with the CategoryContext fetch after mount, and the stack only measures
        116px once it lands, so the strip is already tall enough to hold it and
        the footer never shifts down under the reader.
      */}
      <div className="bg-brand-gradient relative w-full overflow-hidden text-ink">
        <div
          aria-hidden
          className="pattern-weave pattern-on-brand absolute inset-0"
        />

        <div className="relative mx-auto flex min-h-32 max-w-7xl flex-col items-center justify-center gap-3 px-5 py-6 text-center md:px-8 lg:px-12">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/70">
            Questions about a course?
          </span>

          {phone ? (
            <div className="flex items-center gap-3">
              <a
                href={`tel:+91${phone}`}
                onClick={() => window.dataLayer?.push({ event: 'phone_click' })}
                className="group inline-flex h-12 items-center gap-3 rounded-full border-2 border-ink/30 pl-1.5 pr-5 transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-ink-foreground"
              >
                <span className="inline-flex size-9 items-center justify-center rounded-full bg-ink text-ink-foreground transition-colors duration-300 group-hover:bg-ink-foreground group-hover:text-ink">
                  <Phone size={17} strokeWidth={2.25} />
                </span>
                <span className="font-display text-lg font-semibold tracking-tight md:text-xl">
                  +91 {phone}
                </span>
              </a>

              <a
                href={whatsAppHref(phone)}
                onClick={() =>
                  window.dataLayer?.push({ event: 'whatsapp_click' })
                }
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
                className="inline-flex size-12 items-center justify-center rounded-full border-2 border-ink/30 transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-ink-foreground"
              >
                <WhatsAppIcon className="size-5" />
              </a>
            </div>
          ) : null}
        </div>
      </div>

      <Footer />
      {phone ? <WhatsAppButton phone={phone} /> : null}
    </div>
  );
};

export default AppLayout;
